(() => {
  const CLASS = 'sp-scroll-locked';
  const root = document.documentElement;

  // Shopify keeps these in the DOM for the life of the page and toggles display.
  const CONSENT = ['#shopify-pc__banner', '#shopify-pc__prefs'];

  // getClientRects().length is 0 for a display:none element and non-zero for a
  // rendered one, which is the question actually being asked — is this thing on
  // screen — and is immune to however the hiding was expressed.
  //
  // NOT offsetParent: it is null for any position:fixed element even when that
  // element is plainly visible, and BOTH consent surfaces are position:fixed.
  // A first draft used it and would have silently never fired.
  const isVisible = (el) => !!(el && el.getClientRects().length > 0);

  // The vendor's shared modal scrim. The cart drawer locks `html` directly, so
  // it never trips the body test above and would otherwise be the one modal
  // .js-overlay from visibility:hidden/opacity:0 to visible/opacity:1, and
  // gives cart-drawer an `open` attribute + aria-hidden="false".
  //
  // Safe from feedback: nothing in this file's CSS touches .js-overlay's
  // visibility, so reading it can never be influenced by the class we set.
  // Reading html's own overflow instead WOULD feed back, since our own rule
  // sets it — which is why that tempting one-liner is not used here.
  const vendorOverlayOpen = () => {
    const o = document.querySelector('.js-overlay');
    return !!o && getComputedStyle(o).visibility === 'visible';
  };

  // Surfaces whose body lock this theme deliberately CANCELS, and which
  // therefore cannot be detected by mirroring body's computed overflow.
  //
  // The mobile nav drawer is the one case so far. The vendor locks scroll with
  // `.reveal-mobile-nav { overflow: hidden }` on body, and that made body a
  // scroll container, which dropped the sticky header to its static position
  // body's authored `overflow-x: clip / overflow-y: visible` back, so the
  // header holds — and the signal this file reads disappears with it. Naming
  // the class here is what keeps the lock, and it is paired with that rule:
  // whichever half is removed, the page is left unlocked.
  //
  // A class rather than a computed value on purpose. The whole point of the
  // CSS rule is that there is no longer an overflow to read.
  const CANCELLED_BODY_LOCKS = ['reveal-mobile-nav'];

  // Overlays that cover the page and set NO lock of their own — same shape as
  // the consent surfaces above, different vendor. Klaviyo's review media
  // theme's authored `clip visible` and html at `hidden auto`, i.e. nothing
  // anywhere asked for a lock, so the page behind the scrim goes on scrolling
  // and the reader loses their place the moment they touch the wheel.
  //
  // Locking HTML rather than body, which is the whole doctrine of this file:
  // a body lock makes body a scroll container and drops every sticky child to
  // its static position. html carries `scrollbar-gutter: stable` (sp-footer.css)
  // so the gutter stays reserved across the lock and the page does not shift
  // Chrome these are usually checked in, since it draws no classic scrollbars
  // at all.
  const UNLOCKED_OVERLAYS = ['.kl_reviews__lightbox_background'];

  const shouldLock = () => {
    // A body-based lock, whatever set it.
    if (getComputedStyle(document.body).overflowY === 'hidden') return true;
    // A body lock this theme cancels so a sticky header survives.
    if (CANCELLED_BODY_LOCKS.some((c) => document.body.classList.contains(c))) return true;
    // A vendor modal that locked html itself.
    if (vendorOverlayOpen()) return true;
    // A third-party overlay that sets no lock of its own.
    if (UNLOCKED_OVERLAYS.some((sel) => isVisible(document.querySelector(sel)))) return true;
    // Shopify's consent surfaces, which set no lock of their own.
    return CONSENT.some((sel) => isVisible(document.querySelector(sel)));
  };

  let applied = false;
  const sync = () => {
    const want = shouldLock();
    if (want === applied) return;
    applied = want;
    root.classList.toggle(CLASS, want);
  };

  // rAF-debounced, so a burst of mutations during a drawer transition costs one
  // computed-style read rather than dozens.
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  };

  // Attribute changes on body cover the class- and inline-style locks; childList
  // covers Shopify injecting its consent elements, which are direct children of
  // body. Attributes on those two elements cover their own show/hide. Scoped
  // this tightly on purpose — a subtree observer on a storefront is expensive.
  const observe = () => {
    new MutationObserver(schedule).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
    });

    // Mutations alone are not enough. The lock state can change with no mutation
    // on body or on either consent element at all — a media query flipping
    // body's overflow on resize, a stylesheet arriving late, a rule-driven lock
    // rather than a class or inline one. A release test caught exactly this:
    // the class stayed on after the condition had cleared, because nothing the
    // observers watch had changed. So also re-check on the interactions that
    // actually open and close things. Each is one rAF-debounced computed-style
    // read, and `sync` exits immediately when the answer has not changed.
    ['click', 'keydown', 'transitionend', 'resize'].forEach((type) => {
      const target = type === 'resize' ? window : document;
      target.addEventListener(type, schedule, { capture: true, passive: true });
    });

    const overlay = document.querySelector('.js-overlay');
    if (overlay) {
      new MutationObserver(schedule).observe(overlay, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    CONSENT.forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) {
        new MutationObserver(schedule).observe(el, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    });
    sync();
  };

  // Shopify injects the consent elements asynchronously, so re-attach their
  // observers once they exist rather than only at load. Bounded, not a poll
  // that runs forever.
  const attachConsentObservers = () => {
    let tries = 0;
    const tick = () => {
      CONSENT.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el && !el.dataset.spScrollLockObserved) {
          el.dataset.spScrollLockObserved = '1';
          new MutationObserver(schedule).observe(el, {
            attributes: true,
            attributeFilter: ['class', 'style'],
          });
          sync();
        }
      });
      if (++tries < 20) setTimeout(tick, 500);
    };
    tick();
  };

  const start = () => {
    observe();
    attachConsentObservers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
